import React from "react";
import { Box, CircularProgress, Grid, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { useAdminPlatformKpiQuery } from "../../../../../query/hooks/useAdminPlatformKpiQuery";

const useStyles = makeStyles(() => ({
  container: {
    padding: "12px 16px",
    maxWidth: "1190px",
    margin: "0 auto",
    width: "100%",
  },
  title: {
    fontSize: "22px",
    fontWeight: 700,
    color: "#ffffff",
    marginBottom: 16,
  },
  subtitle: {
    fontSize: "13px",
    color: "#A1A1AA",
    marginBottom: 18,
  },
  card: {
    background: "#15151A",
    border: "1px solid #2A2A31",
    borderRadius: 10,
    padding: 16,
    minHeight: 112,
  },
  cardLabel: {
    color: "#A1A1AA",
    fontSize: 12,
    marginBottom: 8,
  },
  cardValue: {
    color: "#ffffff",
    fontSize: 24,
    fontWeight: 700,
    lineHeight: "30px",
  },
  cardMeta: {
    color: "#C9CAD1",
    fontSize: 12,
    marginTop: 8,
  },
  centerState: {
    minHeight: 180,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#A1A1AA",
  },
}));

const formatCurrency = (value: number) =>
  `$${Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
const formatPercent = (value: number) => `${Number(value || 0).toFixed(1)}%`;

const PlatformKPIMetrics = () => {
  const classes = useStyles();
  const { data, isPending, isError } = useAdminPlatformKpiQuery();
  const kpis = data?.kpis;
  const conversion = data?.conversion;

  if (isPending) {
    return (
      <Box className={classes.container}>
        <Box className={classes.centerState}>
          <CircularProgress size={30} style={{ color: "#FE5A97" }} />
        </Box>
      </Box>
    );
  }

  if (isError || !kpis) {
    return (
      <Box className={classes.container}>
        <Box className={classes.centerState}>
          <Typography>Failed to load platform KPI metrics</Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box className={classes.container}>
      <Typography className={classes.title}>Platform KPI Metrics</Typography>
      <Typography className={classes.subtitle}>
        Core operational and monetization indicators for admin monitoring.
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <Box className={classes.card}>
            <Typography className={classes.cardLabel}>Online Creators</Typography>
            <Typography className={classes.cardValue}>
              {Number(kpis.totalOnlineCreators || 0).toLocaleString()}
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Box className={classes.card}>
            <Typography className={classes.cardLabel}>Active Creators</Typography>
            <Typography className={classes.cardValue}>
              {Number(kpis.totalActiveCreators || 0).toLocaleString()}
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Box className={classes.card}>
            <Typography className={classes.cardLabel}>Registered Users</Typography>
            <Typography className={classes.cardValue}>
              {Number(kpis.totalRegisteredUsers || 0).toLocaleString()}
            </Typography>
            <Typography className={classes.cardMeta}>
              MAU 30d: {Number(kpis.mau30d || 0).toLocaleString()}
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Box className={classes.card}>
            <Typography className={classes.cardLabel}>New Signups</Typography>
            <Typography className={classes.cardValue}>
              {Number(kpis.newSignups?.today || 0).toLocaleString()}
            </Typography>
            <Typography className={classes.cardMeta}>
              Week: {Number(kpis.newSignups?.week || 0).toLocaleString()}
            </Typography>
          </Box>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Box className={classes.card}>
            <Typography className={classes.cardLabel}>Revenue This Month</Typography>
            <Typography className={classes.cardValue}>
              {formatCurrency(kpis.revenueThisMonth)}
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Box className={classes.card}>
            <Typography className={classes.cardLabel}>Payouts This Month</Typography>
            <Typography className={classes.cardValue}>
              {formatCurrency(kpis.payoutsThisMonth)}
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Box className={classes.card}>
            <Typography className={classes.cardLabel}>Pending Payouts</Typography>
            <Typography className={classes.cardValue}>
              {formatCurrency(kpis.pendingPayouts?.amount)}
            </Typography>
            <Typography className={classes.cardMeta}>
              Requests: {Number(kpis.pendingPayouts?.count || 0).toLocaleString()}
            </Typography>
          </Box>
        </Grid>

        <Grid item xs={12} md={6}>
          <Box className={classes.card}>
            <Typography className={classes.cardLabel}>
              Conversion Funnel (visitor - signup - paid)
            </Typography>
            <Typography className={classes.cardValue}>
              {Number(conversion?.totals?.visits || 0).toLocaleString()}
              {" -> "}
              {Number(conversion?.totals?.signups || 0).toLocaleString()}
              {" -> "}
              {Number(conversion?.totals?.paid || 0).toLocaleString()}
            </Typography>
            <Typography className={classes.cardMeta}>
              Signup rate: {formatPercent((Number(conversion?.totals?.signupRate || 0) || 0) * 100)}
              {" | "}
              Pay rate: {formatPercent((Number(conversion?.totals?.payRate || 0) || 0) * 100)}
            </Typography>
          </Box>
        </Grid>

      </Grid>
    </Box>
  );
};

export default PlatformKPIMetrics;
